import { noop } from 'lodash';
import {
  ActionWithPayload,
  ActionWithoutPayload,
  action$,
  dispatchAction,
  isValidRxBeachAction,
  routine,
  subscribeRoutine,
  ofType,
} from '@ardoq/rxbeach';
import { filter, first, firstValueFrom, tap } from 'rxjs';
import {
  BridgeClosedPayload,
  bridgeClosed,
  bridgeOpened,
  isActionOfType,
  requestBridge,
  startRelay,
} from '../actions';
import { PortalConfig } from '../types';
import {
  isAutomatedTestMode,
  isDevelopmentMode,
  isRunningInArdoqFront,
} from '../env';
import { assertAllActionCreatorsAreValid } from '../utils';

type BridgeWindowRefs = {
  currentWindow: Window;
  externalWindow: Window;
};

type BridgeConfig = BridgeWindowRefs & {
  portalConfig: PortalConfig;
};

type DebugConfig = {
  logTag: string;
  debug?: boolean;
};

export type PostMessagePortalConfig = BridgeConfig & DebugConfig;

type ConnectionStatus = 'listening' | 'listening-and-relaying' | 'closed';

const getExternalWindowType = ({
  currentWindow,
  externalWindow,
}: BridgeWindowRefs) => {
  if (externalWindow.opener === currentWindow) {
    return 'popout';
  }
  if (externalWindow.top === currentWindow) {
    return 'iframe';
  }
  return 'main-window';
};

const getCurrentWindowType = ({
  currentWindow,
  externalWindow,
}: BridgeWindowRefs) => {
  const externalWindowType = getExternalWindowType({
    externalWindow,
    currentWindow,
  });
  if (externalWindowType !== 'main-window') {
    return 'main-window';
  }
  if (currentWindow.opener === externalWindow) {
    return 'popout';
  }
  return 'iframe';
};

const shouldCloseExternalWindowOnDisconnect = (bridgeConfig: BridgeConfig) => {
  if (getCurrentWindowType(bridgeConfig) !== 'main-window') return false;
  if (getExternalWindowType(bridgeConfig) !== 'popout') return false;

  const { portalConfig } = bridgeConfig;
  if (portalConfig.type === 'SOURCE_PORTAL') return false;
  return Boolean(portalConfig.closePopoutOnDisconnect);
};

/**
 * PostMessagePortal
 * This is implemented as an abstract class to prevent direct usage because it
 * does not handle "REQUEST_BRIDGE", which is the first step of establishing a
 * bridge.
 * The flow looks like this:
 *
 * Document 1                     Document 2
 *     |-------- REQUEST_BRIDGE ----->|
 *     |--------  START_RELAY*  ----->|
 *     |<-------  START_RELAY   ------|
 *
 *  * "Document 1" needs to send START_RELAY in order to communicate that it
 *    is listening and that "Document 2" can start relaying actions. It does
 *    not matter if it is sent before/after "Document 2" sends its START_RELAY
 *    signal.
 *
 * Keeping the bridge alive has many edge cases based on which external window
 * we are working with.
 * - An iframe reloads/closes with the main window, but can be unmounted without
 *   the "beforeunload" event firing. If unmounted we detect it using
 *   `isExternalWindowAlive()` before sending a message, and close the bridge.
 * - A popout reloads/closes independently of the main window, and is detected
 *   using the "beforeunload" event both cases, but cannot distinguish if it is
 *   closing or reloading when receiving the event. `bridgeClosed()` is sent to
 *   the main window before reloading/closing. In Chrome it is not possible for
 *   the popout to close itself instead of reloading, in which case we ask the
 *   main window to close the popout using the `closePopoutOnDisconnect` config.
 * - The main window reloads/closes independently of the popout, and is detected
 *   using the "beforeunload" event both cases, but cannot distinguish if it is
 *   closing or reloading when receiving the event.`bridgeClosed()` is sent to
 *   the main window before reloading/closing. It is possible to use the
 *   `closePopoutOnDisconnect` config to make the bridge close the popout before
 *   closing/reloading the main window.
 */
export abstract class PostMessagePortal {
  /**
   * foreignAction tracks actions that originate from outside the current
   * document, which are the actions received in the onMessage handler.
   */
  public static toForeignAction = (
    action: ActionWithPayload<unknown> | ActionWithoutPayload
  ) => {
    return {
      ...action,
      meta: { ...action.meta, foreignAction: true },
    };
  };

  public static isForeignAction = (
    action: ActionWithPayload<unknown> | ActionWithoutPayload
  ) => {
    // @ts-expect-error custom meta is not supported by rxbeach
    return Boolean(action.meta.foreignAction);
  };

  private portalConfig: PortalConfig;
  private currentWindow: Window;
  private externalWindow: Window;
  private externalWindowInitOrigin: string;
  private logTag: string;
  private debug?: boolean;

  private cleanupFns: VoidFunction[];
  private connectionStatus: ConnectionStatus;

  constructor(config: PostMessagePortalConfig) {
    this.portalConfig = config.portalConfig;
    this.currentWindow = config.currentWindow;
    this.externalWindow = config.externalWindow;
    this.externalWindowInitOrigin = this.externalWindow.location.origin;
    this.logTag = config.logTag;
    this.debug = config.debug;

    this.colorizedDebugLog('Initiating PostMessagePortal');
    if (
      window !== this.currentWindow &&
      !isAutomatedTestMode(this.currentWindow)
    ) {
      throw new Error(
        'PostMessageBridge: currentWindow does not reference the global window variable'
      );
    }

    // Set up action listener, but await initializing the relay functions until
    // the "startRelay" action is received and we know the externalWindow is
    // ready for them.
    const cleanupActionListener = this.setupActionListener();
    this.cleanupFns = [cleanupActionListener];
    this.connectionStatus = 'listening';

    this.currentWindow.addEventListener('beforeunload', this.close);
  }

  private getBridgeConfig = (): BridgeConfig => {
    return {
      portalConfig: this.portalConfig,
      currentWindow: this.currentWindow,
      externalWindow: this.externalWindow,
    };
  };

  /**
   * Close the portal and notify the external window that the bridge is closing.
   */
  public close = () => {
    const { portalConfig } = this;
    const bridgeConfig = this.getBridgeConfig();

    const payload: BridgeClosedPayload = {
      id: portalConfig.id,
      documentType: getCurrentWindowType(bridgeConfig),
    };

    // Notify _externalWindow_ that the bridge is closing from this side
    this.postMessageOverBridge(bridgeClosed(payload));

    // Cleanup bridge on _this_ side
    this.onBridgeClosed(payload);
  };

  /**
   * Cleanup portal from current window
   * The BridgeClosedPayload can be used to detect if it was this window or the
   * external window that closed the bridge, and what kind of window it was.
   */
  private onBridgeClosed = (payload: BridgeClosedPayload) => {
    this.colorizedDebugLog('Bridge closed', payload);

    // Notify _currentWindow_ that the bridge has been closed
    dispatchAction(bridgeClosed(payload));

    this.cleanupPortalListeners();

    const bridgeConfig = this.getBridgeConfig();
    if (shouldCloseExternalWindowOnDisconnect(bridgeConfig)) {
      this.colorizedDebugLog('Closing popout window');
      this.externalWindow.close();
    }
  };

  /**
   * Cleanup action/observable listeners used by the portal
   */
  private cleanupPortalListeners = () => {
    this.colorizedDebugLog('PostMessagePortal closed');
    this.cleanupFns.forEach(cleanup => cleanup());
    this.cleanupFns = [];
    this.connectionStatus = 'closed';
  };

  /**
   * Helper function to manage the timing of actions that rely on an open
   * bridge.
   */
  public opened = () => {
    if (this.connectionStatus === 'listening-and-relaying') {
      return Promise.resolve();
    }
    return firstValueFrom(action$.pipe(ofType(bridgeOpened))).catch(noop);
  };

  /**
   * Check if the external window is still alive.
   * This is particularly important for iframes which do not fire "beforeunload"
   * when they are unmounted from the DOM.
   *
   * To perform this check reliably we need to accommodate for differences in
   * browser implementations, but the key is to check:
   * - value of externalWindow.location.origin
   * - dimensions of externalWindow.screen
   * However, even with these checks there is no guarantee that the window is
   * alive. A page reload can for example trick this check, causing the portal
   * to send messages to a window without an open portal.
   */
  private isExternalWindowAlive = () => {
    try {
      // Browser variations:
      // - Firefox => location.origin=""
      // - Safari => location.origin="null"
      // - Chrome => location.origin=undefined
      if (
        this.externalWindow.location.origin !== this.externalWindowInitOrigin
      ) {
        return false;
      }

      // Browser variations:
      // - Firefox => throw Error()
      // - Safari => { height: 0, width: 0}
      // - Chrome => { height: 0, width: 0}
      const { width = 0, height = 0 } = this.externalWindow.screen;
      const hasNonZeroDimension = 0 < width + height;

      return hasNonZeroDimension;
    } catch (error) {
      // If any check throws we interpret it as the window is closed.
      return false;
    }
  };

  public postMessageOverBridge = (
    action: ActionWithPayload<unknown> | ActionWithoutPayload
  ) => {
    if (!this.isExternalWindowAlive()) {
      this.colorizedDebugLog('External window is not alive');
      const bridgeConfig = this.getBridgeConfig();

      this.onBridgeClosed({
        id: this.portalConfig.id,
        documentType: getExternalWindowType(bridgeConfig),
      });
      return;
    }

    this.colorizedDebugLog(
      `postMessageOverBridge({type: "${action.type}"})`,
      action.payload
    );

    if (
      isDevelopmentMode() &&
      this.connectionStatus !== 'listening-and-relaying' &&
      action.type !== requestBridge.type &&
      action.type !== startRelay.type
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `postMessageOverBridge() was called before the bridge was ready`,
        action
      );
    }

    this.externalWindow.postMessage(action);
  };

  private setupActionRelayer = () => {
    const actionCreators = this.portalConfig.actions ?? [];

    if (isDevelopmentMode()) {
      // development runtime validation for improved DX
      assertAllActionCreatorsAreValid(actionCreators);
    }

    const actionRelayRoutine = routine(
      ofType(...actionCreators),
      filter(action => !PostMessagePortal.isForeignAction(action)),
      tap(this.postMessageOverBridge)
    );

    const actionSub = subscribeRoutine(action$, actionRelayRoutine);

    return () => {
      actionSub.unsubscribe();
    };
  };

  private setupStreamRelayer = () => {
    const streamConfigs = this.portalConfig.streamSubscriptions ?? [];

    const subscriptions = streamConfigs.map(config => {
      const { action, mode = 'continuous' } = config;
      let observable = config.stream;

      // If only an initial stream sync is wanted
      if (mode === 'first') {
        observable = observable.pipe(first());
      }

      observable = observable.pipe(
        tap(payload => {
          this.postMessageOverBridge(action(payload));
        })
      );

      // Streams execute synchronously, so the "tap" function is run before
      // the mapper function returns a value!
      return observable.subscribe(noop);
    });

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  };

  /**
   * Listens for actions posted TO currentWindow FROM externalWindow via the
   * postMessage api. It's important to note that a lot of misc messages flow
   * through this channel as it's shared with the user's extensions and other
   * bridges.
   */
  private setupActionListener = () => {
    this.currentWindow.addEventListener('message', this.onMessage);
    return () => {
      this.currentWindow.removeEventListener('message', this.onMessage);
    };
  };

  /**
   * Handle incoming message, filtering out irrelevant events and validating
   * the source of the event.
   * A portal knows which source it expects to receive messages from, and so
   * it will not act on any messages that are not from that window.
   */
  private onMessage = ({ source, data: action }: MessageEvent<any>) => {
    if (source !== this.externalWindow) {
      return;
    }

    if (!isValidRxBeachAction(action)) {
      return;
    }

    this.colorizedDebugLog(`Received action "${action.type}"`, action);

    // Await START_RELAY action to start relay functions
    if (
      this.connectionStatus === 'listening' &&
      isActionOfType(startRelay, action) &&
      action.payload.portalConfigId === this.portalConfig.id
    ) {
      this.colorizedDebugLog('Starting relay');
      const cleanupActionRelay = this.setupActionRelayer();

      // With the "ActionRelay" active the bridge is "open" in both directions
      // so we announce it on "this" side of the bridge.
      this.connectionStatus = 'listening-and-relaying';
      dispatchAction(bridgeOpened());

      // StreamRelay is started after the bridge is opened because it does not
      // make sense to start sending user specified actions until it is open
      const cleanupStreamRelay = this.setupStreamRelayer();

      // This is an edge case due to observable subscriptions running
      // synchronously, making it possible for the initial stream to close the
      // connection before the cleanupFns is updated. In this case we have to
      // handle the cleanup manually.
      if ((this.connectionStatus as ConnectionStatus) === 'closed') {
        cleanupActionRelay();
        cleanupStreamRelay();
        return;
      }

      this.cleanupFns = [
        ...this.cleanupFns,
        cleanupActionRelay,
        cleanupStreamRelay,
      ];
      return;
    }

    // Handle graceful closing of externalWindow
    if (isActionOfType(bridgeClosed, action)) {
      this.colorizedDebugLog('Received "bridgeClosed" action');
      this.onBridgeClosed(action.payload);
      return;
    }

    // Do not relay bridge specific actions
    if (isActionOfType(requestBridge, action)) {
      return;
    }

    dispatchAction(PostMessagePortal.toForeignAction(action));
  };

  /**
   * This is a helper function that colorizes the two sides of the bridge to
   * aid with debugging. It will only print messages in development mode, and
   * if config contains `{debug: true, logTag?: string}`
   *
   * Note that `debug` has to be set individually on both sides of the bridge.
   *
   * @param message The message you want to print
   * @param args Any parameters you want to print alongside your message
   * @returns void
   */
  protected colorizedDebugLog = (message: string, ...args: any[]) => {
    if (!isDevelopmentMode() || this.debug !== true) {
      return;
    }

    const color = isRunningInArdoqFront(this.currentWindow)
      ? 'background-color:PaleGreen;color:black;'
      : 'background-color:tomato;color:black;';

    // eslint-disable-next-line no-console
    console.debug(`%c[${this.logTag}] ${message}`, color, ...args);
  };
}
